import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { BehaviorSubject, EMPTY, Observable, timer } from "rxjs";
import {
  catchError,
  distinctUntilChanged,
  map,
  shareReplay,
  switchMap,
} from "rxjs/operators";
import { buildDate as clientBuildDate } from "../../../environments/version";

export interface VersionInfo {
  version: string;
  buildDate: string;
}

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

@Injectable({
  providedIn: "root",
})
export class VersionService {
  private readonly http = inject(HttpClient);

  private readonly clientBuildTime = Date.parse(clientBuildDate);

  // Manual trigger for version checks (used on chunk load errors)
  private readonly manualCheck$ = new BehaviorSubject<void>(undefined);

  private readonly serverVersion$ = this.manualCheck$.pipe(
    switchMap(() =>
      timer(0, CHECK_INTERVAL_MS).pipe(switchMap(() => this.fetchVersionInfo())),
    ),
    distinctUntilChanged((prev, curr) => prev?.buildDate === curr?.buildDate),
    shareReplay(1),
  );

  readonly updateAvailable = toSignal(
    this.serverVersion$.pipe(
      map((serverVersion) => {
        if (!serverVersion?.buildDate) return false;
        const serverBuildTime = Date.parse(serverVersion.buildDate);
        if (Number.isNaN(serverBuildTime) || Number.isNaN(this.clientBuildTime)) {
          return false;
        }
        return serverBuildTime > this.clientBuildTime;
      }),
      distinctUntilChanged(),
    ),
    { initialValue: false },
  );

  readonly latestVersion = toSignal(this.serverVersion$, { initialValue: null });

  readonly currentBuildDate = clientBuildDate;

  /**
   * Manually trigger a version check (called on chunk load errors).
   */
  checkForUpdates(): void {
    this.manualCheck$.next();
  }

  reloadApp(): void {
    window.location.reload();
  }

  private fetchVersionInfo(): Observable<VersionInfo | null> {
    return this.http.get<VersionInfo>("/assets/version.json").pipe(
      catchError((error) => {
        console.warn("Failed to fetch server version info:", error);
        return EMPTY;
      }),
    );
  }
}
